// /english/:slug — one lesson: read the story, listen to it, see the phrases you
// are meant to come away with, then go and speak.
//
// The order on the page is the order of use: audio and text first (cheap, no
// microphone), phrases second, the coach last behind a button — it bills per
// audio minute, so it should be a deliberate click rather than the thing that
// happens when the page loads.

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchTopic, fetchVocab } from "../services/englishApi";
import CoachShell from "../components/english/CoachShell";
import PhraseList from "../components/english/PhraseList";
import StoryAudio from "../components/english/StoryAudio";

export default function EnglishLessonPage() {
  const { slug } = useParams();
  const [lesson, setLesson] = useState(null);
  const [status, setStatus] = useState("loading");
  const [vocab, setVocab] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    fetchTopic(slug)
      .then((data) => {
        if (cancelled) return;
        setLesson(data);
        setStatus("ready");
      })
      .catch(() => !cancelled && setStatus("error"));
    // The drill's counts, for the card below. Fetched separately and allowed to
    // fail quietly: the drill is not part of this lesson, and its being
    // unavailable must not take the story down with it.
    fetchVocab(1)
      .then((data) => !cancelled && setVocab(data.counts))
      .catch(() => !cancelled && setVocab(null));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const practiceLink = `/english/${encodeURIComponent(slug)}/practice`;

  return (
    <CoachShell
      back={{ to: "/english", label: "All lessons" }}
      title={lesson?.title || (status === "error" ? "Not found" : "Loading…")}
      subtitle={lesson?.theme}
      actions={
        status === "ready" && (
          // Cheapest first, left to right: the drill is silent and free, the
          // coach bills per minute. The drill is the quieter of the two visually
          // for the same reason — it should not compete with the primary action.
          <div className="flex items-center gap-2">
            {vocab && vocab.total > 0 && (
              <Link
                to="/english/pronunciation"
                className="inline-flex items-center rounded-lg border border-slate-700 px-3 py-2 text-sm font-medium text-slate-300 transition hover:border-sky-500/50 hover:text-sky-300"
              >
                🗣 <span className="ml-1.5 hidden sm:inline">Pronunciation drill</span>
                <span className="ml-1.5 text-[11px] tabular-nums text-slate-500">
                  {vocab.due}
                </span>
              </Link>
            )}
            <Link
              to={practiceLink}
              className="inline-flex items-center rounded-lg bg-sky-500/90 px-4 py-2 text-sm font-medium text-slate-950 transition hover:bg-sky-400"
            >
              🎙 <span className="ml-1.5">Start voice practice</span>
            </Link>
          </div>
        )
      }
    >
      {status === "loading" && <p className="text-sm text-slate-500">Loading…</p>}

      {status === "error" && (
        <div className="text-sm text-rose-400">
          <p>No story named “{slug}”.</p>
          <Link to="/english" className="mt-2 inline-block text-slate-400 underline">
            ← back to the lessons
          </Link>
        </div>
      )}

      {status === "ready" && lesson && (
        <div className="space-y-8">
          <StoryAudio
            slug={lesson.slug}
            hasAudio={lesson.has_audio}
            audioUrl={lesson.audio_url}
          />

          {/* The story is plain prose written for reading aloud, so paragraph
              breaks are all the structure it has — and all it needs. */}
          <article className="space-y-4">
            {lesson.story
              .split(/\n{2,}/)
              .map((para) => para.trim())
              .filter(Boolean)
              .map((para, i) => (
                <p key={i} className="text-[15px] leading-relaxed text-slate-300 lg:text-base lg:leading-relaxed">
                  {para}
                </p>
              ))}
          </article>

          <PhraseList coverage={lesson.coverage} />

          {/* Repeated at the bottom: after reading the story and the phrase list
              you are at the end of a long page, which is exactly when you are
              ready to talk. */}
          <div className="flex flex-col gap-3 rounded-xl border border-sky-500/20 bg-sky-500/5 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-medium text-slate-200">Ready to speak?</div>
              <div className="text-xs text-slate-500">
                The coach drills these phrases with you and bills per minute.
              </div>
            </div>
            <Link
              to={practiceLink}
              className="inline-flex shrink-0 items-center rounded-lg bg-sky-500/90 px-4 py-2 text-sm font-medium text-slate-950 transition hover:bg-sky-400"
            >
              🎙 Start voice practice
            </Link>
          </div>
        </div>
      )}
    </CoachShell>
  );
}
